import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { Oriented } from "@/data/constants/oriented";
import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

type HistoricalRatingParams = {
  symbol: string;
  oriented: Omit<Oriented, "dividend">;
  lang: Lang;
};

const historicalRatingResponseSchema = z.object({
  data: z.array(
    z.object({
      date: z.string(),
      rating: z.number(),
      price: z.number(),
    })
  ),
  message: z.string(),
});

export type HistoricalRatingData = z.infer<
  typeof historicalRatingResponseSchema
>["data"][number];

export const getHistoricalRating = async ({
  symbol,
  oriented,
  lang,
}: HistoricalRatingParams): Promise<HistoricalRatingData[]> => {
  const endpoint = `/historical-rating/${symbol}/${oriented as string}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });
  if (!res.ok) {
    if (res.status === 409) {
      // no historical data
      return [];
    }
    throw new Error(`API failed (${endpoint})`);
  }
  return historicalRatingResponseSchema.parse(await res.json()).data;
};

export const useHistoricalRating = ({
  symbol,
  oriented,
}: Omit<HistoricalRatingParams, "lang">) => {
  const { lang } = useLang();
  return useQuery(
    ["historical-rating", symbol, oriented, lang],
    () => getHistoricalRating({ symbol, oriented, lang }),
    { enabled: !!symbol && !!oriented }
  );
};
